import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Edit,
  Eye,
  Filter,
  HelpCircle,
  Lock,
  Plus,
  ShieldCheck,
  TrendingUp,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import {
  AssessmentAudit,
  canViewSDLDetails,
  isAdmin,
  SDLAssessmentPeriodForProduct,
  User,
} from "../types";
import { canReopenSubmittedAssessment } from "../utils/sdlAssessmentEditing";
import CopyAssessmentModal from "./CopyAssessmentModal";
import LastModified from "./LastModified";
import SDLAssessmentEditModal from "./SDLAssessmentEditModal";
import { SDLScoringGuideModal } from "./SDLScoringGuideModal";

interface SDLAssessmentPanelProps {
  productId: string;
  productName: string;
  productOwnerId: string;
  userId?: string | undefined;
  user: User | null;
  sdlMaturityScore?: number | null;
  sdlPracticeScores?: string | Record<string, number> | null;
  sdlAssessmentDate?: string | null;
  assessmentId?: string;
  periods: SDLAssessmentPeriodForProduct[];
  periodsLoading: boolean;
  craApplicability?: string | null;
  /* Audit trail per assessment, from the product's assessment-status query.
     Undefined when the assessment does not exist, or -- for sdlAudit -- when
     the viewer's role is not allowed to see SDL detail. */
  craApplicabilityAudit?: AssessmentAudit | undefined;
  craComplianceAudit?: AssessmentAudit | undefined;
  sdlAudit?: AssessmentAudit | undefined;
  /* Rendered inside the "Assessment Status" card, after the CRA sections, so
     the Data Act status sits alongside the other assessment statuses rather
     than in a detached card further down the page. */
  dataActSlot?: React.ReactNode;
}

export default function SDLAssessmentPanel({
  productId,
  productName,
  productOwnerId,
  userId,
  user,
  sdlMaturityScore,
  sdlPracticeScores,
  sdlAssessmentDate,
  assessmentId,
  periods,
  periodsLoading,
  craApplicability,
  craApplicabilityAudit,
  craComplianceAudit,
  sdlAudit,
  dataActSlot,
}: SDLAssessmentPanelProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isScoringModalOpen, setIsScoringModalOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<
    "all" | "submitted" | "draft" | "missing"
  >("all");
  const [copyCRAOpen, setCopyCRAOpen] = useState(false);
  const [copySDLOpen, setCopySDLOpen] = useState(false);
  const [copySDLPeriod, setCopySDLPeriod] = useState("");

  const canEditAssessments = productOwnerId === userId || isAdmin(user);
  // Any signed-in user (e.g. a consultant acting as scribe) may fill in and
  // save an SDL draft; submission stays owner/admin only.
  const canFillSDLDrafts = !!userId;
  const showAdminDetails = canViewSDLDetails(user);

  // Find the current active period (explicitly marked, or first open, or latest)
  const currentPeriod =
    periods.find((p) => p.isCurrent) ||
    periods.find((p) => p.isOpen) ||
    periods[0];

  const hasScore = sdlMaturityScore !== null && sdlMaturityScore !== undefined;

  // Parse practice scores
  let practiceScores: Record<string, number> | null = null;
  if (sdlPracticeScores) {
    try {
      practiceScores =
        typeof sdlPracticeScores === "string"
          ? JSON.parse(sdlPracticeScores)
          : sdlPracticeScores;
    } catch {
      practiceScores = null;
    }
  }

  const getScoreColor = (score: number) => {
    if (score < 1.0)
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700";
    if (score < 2.0)
      return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-700";
    if (score < 3.0)
      return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-700";
    return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700";
  };

  const formatScore = (score: number) => {
    const formatted = score.toFixed(2);
    return formatted.replace(/0+$/, "").replace(/\.$/, ".0");
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div
        className={`flex items-center justify-between ${expanded ? "mb-5" : ""}`}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          Assessment Status
        </button>
        {isAdmin(user) && (
          <div className="flex items-center gap-2">
            <Link
              to="/assessments/import"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-md hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
              title="Import assessment data from Excel"
            >
              <Upload className="h-4 w-4" />
              Import
            </Link>
          </div>
        )}
      </div>

      {expanded && (
        <>
          {/* CRA Applicability Assessment */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-5">
            <div className="flex items-center mb-2">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                CRA Applicability Assessment
              </h3>
            </div>
            <div className="space-y-2">
              {craApplicability && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Result:
                  </span>
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded-full ${
                      craApplicability === "not_in_scope"
                        ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                        : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
                    }`}
                  >
                    {craApplicability.replace(/_/g, " ")}
                  </span>
                </div>
              )}
              <LastModified audit={craApplicabilityAudit} />
              {canEditAssessments ? (
                <div className="flex gap-2">
                  <Link
                    to={`/products/${productId}/cra-assessment`}
                    className="btn btn-primary flex-1 text-sm"
                  >
                    {craApplicability && craApplicability !== "not_assessed"
                      ? "Retake Assessment"
                      : "Start Assessment"}
                  </Link>
                  {craApplicability && craApplicability !== "not_assessed" && (
                    <button
                      onClick={() => setCopyCRAOpen(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Copy answers to other products"
                    >
                      <Copy className="h-4 w-4" />
                      Copy
                    </button>
                  )}
                </div>
              ) : (
                <button
                  disabled
                  className="btn btn-primary w-full text-sm opacity-50 cursor-not-allowed"
                  title="Only the product owner can start assessments"
                >
                  {craApplicability && craApplicability !== "not_assessed"
                    ? "Retake Assessment"
                    : "Start Assessment"}
                </button>
              )}
            </div>
          </div>

          {/* CRA Compliance Survey */}
          <CRAComplianceCard
            productId={productId}
            productName={productName}
            canEditAssessments={canEditAssessments}
            audit={craComplianceAudit}
          />

          {/* Data Act Assessment */}
          {dataActSlot && <div className="mb-5">{dataActSlot}</div>}

          {/* SDL Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                SDL Assessment
              </h2>
              <button
                onClick={() => setIsScoringModalOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors ml-1"
                title="How Scoring Works"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                <span className="underline underline-offset-2">
                  How Scoring Works
                </span>
              </button>
            </div>
            {showAdminDetails && (
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors"
              >
                <Edit className="h-3 w-3 mr-1" />
                Admin Edit
              </button>
            )}
          </div>

          {periodsLoading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </p>
            </div>
          ) : (
            <>
              {/* ─── Two-Card Layout ─── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {/* ── Card 1: Current Assessment ── */}
                {(() => {
                  // Determine current period status
                  const cp = currentPeriod;
                  const cpStatus = cp
                    ? cp.hasAssessment && cp.assessmentStatus === "submitted"
                      ? "submitted"
                      : cp.hasAssessment && cp.assessmentStatus === "draft"
                        ? "draft"
                        : cp.isOpen
                          ? "not_started"
                          : "closed"
                    : null;

                  const dueLabel = cp
                    ? `Due end of Q${cp.quarter} ${cp.year}`
                    : null;

                  // Draft progress info
                  const draftProgress =
                    cpStatus === "draft" &&
                    cp?.answeredCount != null &&
                    cp?.totalQuestions != null &&
                    cp.totalQuestions > 0
                      ? Math.round((cp.answeredCount / cp.totalQuestions) * 100)
                      : null;

                  const lastSaved =
                    cpStatus === "draft" && cp?.lastSavedAt
                      ? new Date(cp.lastSavedAt)
                      : null;

                  const formatTimeAgo = (date: Date) => {
                    const now = new Date();
                    const diffMs = now.getTime() - date.getTime();
                    const diffMins = Math.floor(diffMs / 60000);
                    if (diffMins < 1) return "just now";
                    if (diffMins < 60) return `${diffMins}m ago`;
                    const diffHours = Math.floor(diffMins / 60);
                    if (diffHours < 24) return `${diffHours}h ago`;
                    const diffDays = Math.floor(diffHours / 24);
                    if (diffDays < 7) return `${diffDays}d ago`;
                    return date.toLocaleDateString();
                  };

                  const statusConfig: Record<
                    string,
                    { label: string; color: string; icon: JSX.Element }
                  > = {
                    not_started: {
                      label: "Not Started",
                      color:
                        "text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300",
                      icon: <Clock className="h-3.5 w-3.5" />,
                    },
                    draft: {
                      label: "Draft In Progress",
                      color:
                        "text-yellow-700 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300",
                      icon: <Edit className="h-3.5 w-3.5" />,
                    },
                    submitted: {
                      label: "Submitted",
                      color:
                        "text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-300",
                      icon: <CheckCircle className="h-3.5 w-3.5" />,
                    },
                    closed: {
                      label: "Closed",
                      color:
                        "text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400",
                      icon: <AlertTriangle className="h-3.5 w-3.5" />,
                    },
                  };

                  const sc = cpStatus ? statusConfig[cpStatus] : null;

                  // CTA for this card
                  const ctaLabel =
                    cpStatus === "draft"
                      ? "Resume Assessment"
                      : cpStatus === "not_started"
                        ? "Start Assessment"
                        : cpStatus === "submitted"
                          ? "View Result"
                          : null;
                  const ctaIcon =
                    cpStatus === "draft" ? (
                      <Edit className="h-4 w-4 mr-1.5" />
                    ) : cpStatus === "not_started" ? (
                      <Plus className="h-4 w-4 mr-1.5" />
                    ) : cpStatus === "submitted" ? (
                      <Eye className="h-4 w-4 mr-1.5" />
                    ) : null;
                  const ctaOwnerOnly = cpStatus !== "submitted";

                  return (
                    <div className="border-l-4 border-l-blue-500 dark:border-l-blue-400 border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-white dark:bg-gray-900 shadow-sm">
                      <p className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-3">
                        Current Assessment
                      </p>

                      {cp ? (
                        <>
                          {/* Period */}
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                            {cp.year}-Q{cp.quarter}
                          </p>

                          {/* Status + Due */}
                          <div className="flex flex-wrap items-center gap-2 mb-4">
                            {sc && (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}
                              >
                                {sc.icon}
                                {sc.label}
                              </span>
                            )}
                            {dueLabel &&
                              cpStatus !== "submitted" &&
                              cpStatus !== "closed" && (
                                <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                                  <Calendar className="h-3 w-3" />
                                  {dueLabel}
                                </span>
                              )}
                          </div>

                          {/* Draft Progress */}
                          {cpStatus === "draft" && draftProgress !== null && (
                            <div className="mb-4">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  Progress
                                </span>
                                <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-300">
                                  {draftProgress}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-yellow-500 dark:bg-yellow-400 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${draftProgress}%` }}
                                />
                              </div>
                              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                {cp?.answeredCount} of {cp?.totalQuestions}{" "}
                                questions answered
                              </p>
                            </div>
                          )}

                          {/* Last Saved */}
                          {cpStatus === "draft" && lastSaved && (
                            <div className="flex items-center gap-1.5 mb-4 text-[11px] text-gray-400 dark:text-gray-500">
                              <Clock className="h-3 w-3" />
                              <span>Last saved {formatTimeAgo(lastSaved)}</span>
                            </div>
                          )}

                          {/* CTA */}
                          {ctaLabel && (
                            <div>
                              {!ctaOwnerOnly || canFillSDLDrafts ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        cpStatus === "submitted"
                                          ? `/products/${productId}/sdl-results?period=${cp.period}`
                                          : `/products/${productId}/sdl-assessment?period=${cp.period}`,
                                      )
                                    }
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors shadow-sm"
                                  >
                                    {ctaIcon}
                                    {ctaLabel}
                                  </button>
                                  {/* Submitted assessments can still be corrected
                                      while their period is open. The primary CTA
                                      above goes to the results view, so without
                                      this there is no route from the product page
                                      back into the form once submitted. */}
                                  {canReopenSubmittedAssessment({
                                    status: cpStatus,
                                    periodIsOpen: cp.isOpen,
                                    canManage: canEditAssessments,
                                  }) && (
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/products/${productId}/sdl-assessment?period=${cp.period}`,
                                        )
                                      }
                                      className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                      title="Edit this submitted assessment"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  )}
                                  {cpStatus === "submitted" &&
                                    canEditAssessments && (
                                      <button
                                        onClick={() => {
                                          setCopySDLPeriod(cp.period);
                                          setCopySDLOpen(true);
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        title="Copy answers to other products"
                                      >
                                        <Copy className="h-4 w-4" />
                                      </button>
                                    )}
                                </div>
                              ) : (
                                <button
                                  disabled
                                  className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white opacity-50 cursor-not-allowed"
                                  title="Only the product owner can start assessments"
                                >
                                  {ctaIcon}
                                  {ctaLabel}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                          No active period
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* ── Card 2: Latest Completed Assessment ── */}
                {(() => {
                  // Find the most recent submitted period
                  const submittedPeriods = periods.filter(
                    (p) =>
                      p.hasAssessment && p.assessmentStatus === "submitted",
                  );
                  const latest = submittedPeriods[0] || null;

                  // Derive recommendation from the lowest practice score
                  let recommendation: string | null = null;
                  if (
                    practiceScores &&
                    Object.keys(practiceScores).length > 0
                  ) {
                    let minPractice = "";
                    let minVal = Infinity;
                    for (const [p, s] of Object.entries(practiceScores)) {
                      const n =
                        typeof s === "number"
                          ? s
                          : parseFloat(s as unknown as string);
                      if (!isNaN(n) && n < minVal) {
                        minVal = n;
                        minPractice = p;
                      }
                    }
                    if (minPractice && minVal <= 2) {
                      recommendation = `${minPractice} needs improvement`;
                    } else if (minPractice && minVal <= 3) {
                      recommendation = `Focus on strengthening ${minPractice}`;
                    }
                  }

                  return (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-white dark:bg-gray-900">
                      <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                        Latest Completed
                      </p>

                      {latest ? (
                        <>
                          {/* Period */}
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {latest.year}-Q{latest.quarter}
                          </p>

                          {/* Score */}
                          {hasScore ? (
                            <div className="flex items-baseline gap-2 mb-3">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-lg text-2xl font-bold border ${getScoreColor(
                                  sdlMaturityScore!,
                                )}`}
                              >
                                {formatScore(sdlMaturityScore!)}
                              </span>
                              <span className="text-sm text-gray-400 dark:text-gray-500">
                                / 4.0
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mb-3">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-300">
                                <CheckCircle className="h-3.5 w-3.5" />
                                Submitted
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                Score pending
                              </span>
                            </div>
                          )}

                          {/* Practice Score Badges */}
                          {practiceScores &&
                            Object.keys(practiceScores).length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {Object.entries(practiceScores).map(
                                  ([practice, score]) => {
                                    const n =
                                      typeof score === "number"
                                        ? score
                                        : parseFloat(
                                            score as unknown as string,
                                          );
                                    if (isNaN(n)) return null;
                                    return (
                                      <span
                                        key={practice}
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getScoreColor(
                                          n,
                                        )}`}
                                        title={`${practice}: ${n.toFixed(1)}`}
                                      >
                                        {practice}: {n.toFixed(1)}
                                      </span>
                                    );
                                  },
                                )}
                              </div>
                            )}

                          {/* Recommendation */}
                          {recommendation && (
                            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 mb-3">
                              <TrendingUp className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                              <p className="text-xs text-amber-700 dark:text-amber-300">
                                {recommendation}
                              </p>
                            </div>
                          )}

                          {/* Audit trail for this assessment. Sits in "Latest
                              Completed" rather than "Current Assessment" because
                              that is the assessment the backend resolves sdlAudit
                              against -- the two are different periods whenever the
                              current one is still a draft. */}
                          <LastModified audit={sdlAudit} className="mb-3" />

                          {/* View Details */}
                          <button
                            onClick={() =>
                              navigate(
                                `/products/${productId}/sdl-results?period=${latest.period}`,
                              )
                            }
                            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Eye className="h-4 w-4 mr-1.5" />
                            View Details
                          </button>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                          <BarChart3 className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
                          <p className="text-sm text-gray-400 dark:text-gray-500">
                            Awaiting first submission
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Assessment History */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                {/* Header Row */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  >
                    {showHistory ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <span>Assessment History ({periods.length})</span>
                  </button>

                  {showHistory && (
                    <div className="flex items-center gap-1.5">
                      <Filter className="h-3.5 w-3.5 text-gray-400" />
                      <select
                        value={historyFilter}
                        onChange={(e) =>
                          setHistoryFilter(
                            e.target.value as
                              | "all"
                              | "submitted"
                              | "draft"
                              | "missing",
                          )
                        }
                        className="text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      >
                        <option value="all">All</option>
                        <option value="submitted">Submitted</option>
                        <option value="draft">Draft</option>
                        <option value="missing">Not Started</option>
                      </select>
                    </div>
                  )}
                </div>

                {showHistory &&
                  (() => {
                    // Sort periods: latest first
                    const sorted = [...periods].sort((a, b) =>
                      a.year !== b.year
                        ? b.year - a.year
                        : b.quarter - a.quarter,
                    );

                    const getEffectiveStatus = (
                      p: SDLAssessmentPeriodForProduct,
                    ) => {
                      if (p.hasAssessment && p.assessmentStatus === "submitted")
                        return "submitted";
                      if (
                        p.hasAssessment &&
                        p.assessmentStatus === "draft" &&
                        !p.isOpen
                      )
                        return "locked";
                      if (p.hasAssessment && p.assessmentStatus === "draft")
                        return "draft";
                      if (!p.hasAssessment && !p.isOpen) return "closed";
                      return "not_started";
                    };

                    // Filter
                    const filtered = sorted.filter((p) => {
                      const s = getEffectiveStatus(p);
                      if (historyFilter === "all") return true;
                      if (historyFilter === "submitted")
                        return s === "submitted";
                      if (historyFilter === "draft") return s === "draft";
                      if (historyFilter === "missing")
                        return (
                          s === "not_started" ||
                          s === "closed" ||
                          s === "locked"
                        );
                      return true;
                    });

                    const statusBadge = (p: SDLAssessmentPeriodForProduct) => {
                      const s = getEffectiveStatus(p);
                      const draftPct =
                        s === "draft" &&
                        p.answeredCount != null &&
                        p.totalQuestions != null &&
                        p.totalQuestions > 0
                          ? Math.round(
                              (p.answeredCount / p.totalQuestions) * 100,
                            )
                          : null;
                      const config: Record<
                        string,
                        { label: string; cls: string; icon: JSX.Element }
                      > = {
                        submitted: {
                          label: "Submitted",
                          cls: "text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300",
                          icon: <CheckCircle className="h-3 w-3" />,
                        },
                        draft: {
                          label:
                            draftPct !== null
                              ? `Draft · ${draftPct}%`
                              : "Draft",
                          cls: "text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-300",
                          icon: <Edit className="h-3 w-3" />,
                        },
                        locked: {
                          label:
                            draftPct !== null
                              ? `Locked · ${draftPct}%`
                              : "Locked",
                          cls: "text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400",
                          icon: <Lock className="h-3 w-3" />,
                        },
                        closed: {
                          label: "Closed",
                          cls: "text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400",
                          icon: <AlertTriangle className="h-3 w-3" />,
                        },
                        not_started: {
                          label: "Not Started",
                          cls: "text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-300",
                          icon: <Clock className="h-3 w-3" />,
                        },
                      };
                      const c = config[s] || config["not_started"]!;
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${c!.cls}`}
                        >
                          {c!.icon}
                          {c!.label}
                        </span>
                      );
                    };

                    const actionButton = (p: SDLAssessmentPeriodForProduct) => {
                      const s = getEffectiveStatus(p);
                      if (s === "submitted") {
                        return (
                          <button
                            onClick={() =>
                              navigate(
                                `/products/${productId}/sdl-results?period=${p.period}`,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                            View
                          </button>
                        );
                      }
                      if (s === "locked") {
                        return (
                          <button
                            onClick={() =>
                              navigate(
                                `/products/${productId}/sdl-assessment?period=${p.period}`,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                            View
                          </button>
                        );
                      }
                      if (s === "draft" && canFillSDLDrafts) {
                        return (
                          <button
                            onClick={() =>
                              navigate(
                                `/products/${productId}/sdl-assessment?period=${p.period}`,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"
                          >
                            <Edit className="h-3 w-3" />
                            Resume
                          </button>
                        );
                      }
                      if (s === "not_started" && p.isOpen && canFillSDLDrafts) {
                        return (
                          <button
                            onClick={() =>
                              navigate(
                                `/products/${productId}/sdl-assessment?period=${p.period}`,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                            Start
                          </button>
                        );
                      }
                      return (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          —
                        </span>
                      );
                    };

                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Period
                              </th>
                              <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Score
                              </th>
                              <th className="text-right py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {filtered.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="py-6 text-center text-sm text-gray-400 dark:text-gray-500"
                                >
                                  No periods match the selected filter
                                </td>
                              </tr>
                            ) : (
                              filtered.map((period) => {
                                const isCurrent =
                                  currentPeriod?.period === period.period;
                                return (
                                  <tr
                                    key={period.period}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                  >
                                    <td className="py-2.5 pr-4">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900 dark:text-gray-100">
                                          {period.year}-Q{period.quarter}
                                        </span>
                                        {isCurrent && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 leading-none">
                                            NOW
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-2.5 pr-4">
                                      {statusBadge(period)}
                                    </td>
                                    <td className="py-2.5 pr-4">
                                      {period.hasAssessment &&
                                      period.assessmentStatus === "submitted" &&
                                      period.maturityScore != null ? (
                                        <span
                                          className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${getScoreColor(
                                            period.maturityScore,
                                          )}`}
                                        >
                                          {formatScore(period.maturityScore)}/4
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400 dark:text-gray-500">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2.5 text-right">
                                      {actionButton(period)}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
              </div>
            </>
          )}
        </>
      )}

      {/* Admin Edit Modal */}
      {showAdminDetails && (
        <SDLAssessmentEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          productId={productId}
          assessmentId={assessmentId}
          currentScore={sdlMaturityScore ?? undefined}
          currentDate={sdlAssessmentDate ?? undefined}
        />
      )}

      {/* How Scoring Works Modal */}
      <SDLScoringGuideModal
        isOpen={isScoringModalOpen}
        onClose={() => setIsScoringModalOpen(false)}
      />

      {/* Copy Assessment Modals */}
      <CopyAssessmentModal
        isOpen={copyCRAOpen}
        onClose={() => setCopyCRAOpen(false)}
        sourceProductId={productId}
        sourceProductName={productName}
        assessmentType="cra"
      />
      <CopyAssessmentModal
        isOpen={copySDLOpen}
        onClose={() => setCopySDLOpen(false)}
        sourceProductId={productId}
        sourceProductName={productName}
        assessmentType="sdl"
        period={copySDLPeriod}
      />
    </div>
  );
}

// ── CRA Compliance Card (sub-component) ──────────────────────────────
function CRAComplianceCard({
  productId,
  productName,
  canEditAssessments,
  audit,
}: {
  productId: string;
  productName: string;
  canEditAssessments: boolean;
  audit?: AssessmentAudit | undefined;
}) {
  const [copyOpen, setCopyOpen] = useState(false);
  const { data, isLoading } = useQuery(
    ["craComplianceAssessment", productId],
    () => apiService.getLatestCRAComplianceAssessment(productId),
    { retry: false, staleTime: 30_000 },
  );

  const assessment = (data?.data as any)?.data;
  const status: string | null = assessment?.status ?? null;

  // Count answers
  let yesCount = 0;
  let totalCount = 0;
  if (assessment?.answers) {
    const answers =
      typeof assessment.answers === "string"
        ? (() => {
            try {
              return JSON.parse(assessment.answers);
            } catch {
              return [];
            }
          })()
        : assessment.answers;
    if (Array.isArray(answers)) {
      totalCount = answers.length;
      yesCount = answers.filter((a: any) => a.answer === "Yes").length;
    }
  }

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-5">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <h3 className="font-medium text-gray-900 dark:text-gray-100">
          CRA Compliance assessment
        </h3>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Loading...
          </span>
        </div>
      ) : status === "submitted" ? (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Status:
            </span>
            <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
              Submitted
            </span>
          </div>
          {totalCount > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Compliance:
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {yesCount}/{totalCount} Yes (
                {totalCount > 0 ? Math.round((yesCount / totalCount) * 100) : 0}
                %)
              </span>
            </div>
          )}
          <div className="flex gap-2 mt-1">
            <Link
              to={`/products/${productId}/cra-compliance`}
              className="btn btn-outline flex-1 text-sm"
            >
              <Eye className="h-4 w-4 mr-1" />
              View Responses
            </Link>
            {canEditAssessments && (
              <button
                onClick={() => setCopyOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title="Copy answers to other products"
              >
                <Copy className="h-4 w-4" />
                Copy
              </button>
            )}
          </div>
        </div>
      ) : status === "draft" ? (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Status:
            </span>
            <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
              Draft In Progress
            </span>
          </div>
          {canEditAssessments ? (
            <Link
              to={`/products/${productId}/cra-compliance`}
              className="btn btn-primary w-full text-sm"
            >
              Continue Assessment
            </Link>
          ) : (
            <button
              disabled
              className="btn btn-primary w-full text-sm opacity-50 cursor-not-allowed"
              title="Only the product owner can continue assessments"
            >
              Continue Assessment
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Assess your product's compliance with the EU Cyber Resilience Act
            requirements.
          </p>
          {canEditAssessments ? (
            <Link
              to={`/products/${productId}/cra-compliance`}
              className="btn btn-primary w-full text-sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Start Assessment
            </Link>
          ) : (
            <button
              disabled
              className="btn btn-primary w-full text-sm opacity-50 cursor-not-allowed"
              title="Only the product owner can start assessments"
            >
              Start Assessment
            </button>
          )}
        </div>
      )}

      {/* Rendered once outside the status branches rather than in each of
          them: it self-hides when there is no assessment, so the "not started"
          branch needs no guard. */}
      <LastModified audit={audit} className="mt-2" />

      <CopyAssessmentModal
        isOpen={copyOpen}
        onClose={() => setCopyOpen(false)}
        sourceProductId={productId}
        sourceProductName={productName}
        assessmentType="cra-compliance"
      />
    </div>
  );
}
